// API client + types for case-matching, candidates, and search.
// Backend mirror: server/services/outlook/caseMatcher.ts + routes/outlook/{match,search}.ts

import { apiRequest } from "./api";

export type CandidateSource = "linked_folder" | "matter_number" | "contact_active" | "contact_recent";

export interface CandidateCase {
  caseId: string;
  caseTitle: string;
  matterNumber: string | null;
  status: string;
  score: number;
  primarySource: CandidateSource;
  reasons: string[];
  updatedAt: string | null; // ISO date string from JSON
}

export interface SearchResultCase {
  caseId: string;
  caseTitle: string;
  matterNumber: string | null;
  status: string;
  updatedAt: string | null;
}

export interface MatchInput {
  fromEmail: string;
  subject?: string;
  bodyPreview?: string;
  parentFolderId?: string;
  emailAccountId?: string;
}

interface MatchResponse {
  match: CandidateCase | null;
  hasCandidates: boolean;
  threshold: number;
}

interface CandidatesResponse {
  candidates: CandidateCase[];
  threshold: number;
}

interface SearchResponse {
  cases: SearchResultCase[];
  total: number;
}

function buildQuery(input: MatchInput, extra: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams();
  params.set("fromEmail", input.fromEmail);
  if (input.subject) params.set("subject", input.subject);
  if (input.bodyPreview) params.set("bodyPreview", input.bodyPreview);
  if (input.parentFolderId) params.set("parentFolderId", input.parentFolderId);
  if (input.emailAccountId) params.set("emailAccountId", input.emailAccountId);
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined) params.set(k, String(v));
  }
  return params.toString();
}

export async function fetchAutoMatch(input: MatchInput, signal?: AbortSignal): Promise<MatchResponse> {
  return apiRequest<MatchResponse>(`/api/outlook/match?${buildQuery(input)}`, { signal });
}

export async function fetchCandidates(input: MatchInput, limit = 5, signal?: AbortSignal): Promise<CandidatesResponse> {
  return apiRequest<CandidatesResponse>(`/api/outlook/candidates?${buildQuery(input, { limit })}`, { signal });
}

export async function searchCases(query: string, signal?: AbortSignal): Promise<SearchResponse> {
  const q = new URLSearchParams({ q: query, limit: "10" }).toString();
  return apiRequest<SearchResponse>(`/api/outlook/search?${q}`, { signal });
}

// Human-readable label per source — used in the candidate-list reason chip.
export function sourceLabel(source: CandidateSource): string {
  switch (source) {
    case "linked_folder": return "uit dossier-folder";
    case "matter_number": return "dossiernummer";
    case "contact_active": return "afzender bekend";
    case "contact_recent": return "recent contact";
  }
}
